import './styles/Maintenance.css';

import { type FC, memo, useEffect, useRef } from 'react';

import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import { prop } from '@ipfabric/utils/fp/prop';
import { Button } from 'components/Button/Button';
import { useGlobalStateContextSelectors } from 'components/GlobalState/GlobalStateModel';
import { setMaintenanceVisibility } from 'components/GlobalState/commands/setMaintenanceVisibility';
import { Particles } from 'components/Particles/Particles';
import { destroyParticles } from 'components/Particles/destroyParticles';
import { showParticles } from 'components/Particles/showParticles';
import { useLocale } from 'containers/App/LocaleContext';
import get from 'lodash/get';
import { useApi } from 'utils/request/useApi';

const PARTICLES_COUNT = 100;
const MAINTENANCE_POLL_TIMEOUT = 10000;

const BareMaintenance: FC = () => {
  const { maintenance: msg } = useLocale();
  const api = useApi();
  const checkMaintenanceTimeout = useRef<number | undefined>(undefined);
  const ctxProps = useGlobalStateContextSelectors({
    dispatch: prop('dispatch'),
  });

  /**
   * When maintenance starts running, poll check for when it is finished.
   *
   * Note: maintenance finish is also detected from job update received from
   * sockets (Maintenance/sagas.js:jobUpdatedSaga), but that does not work
   * when user is not logged in, app is not initialized yet etc.
   */
  const testRunningMaintenance = async () => {
    window.clearTimeout(checkMaintenanceTimeout.current);
    try {
      await api.request({
        url: '/licenses/validity',
        method: 'GET',
      });
      ctxProps.dispatch(setMaintenanceVisibility, { value: false });
    } catch {
      startTimeout();
    }
  };

  const startTimeout = useStableCallback(() => {
    window.clearTimeout(checkMaintenanceTimeout.current);
    checkMaintenanceTimeout.current = window.setTimeout(() => {
      testRunningMaintenance();
    }, MAINTENANCE_POLL_TIMEOUT);
  });

  useEffect(() => {
    showParticles('particles-js', PARTICLES_COUNT);
    startTimeout();
    return () => {
      destroyParticles();
      window.clearTimeout(checkMaintenanceTimeout.current);
    };
  }, []);

  return (
    <Particles
      button={
        <Button
          variant='secondary'
          onClick={testRunningMaintenance}
          type='button'
        >
          {msg.btnRefresh}
        </Button>
      }
      className='maintenance'
      contentClassName='maintenance-content'
      description={get(msg, 'text')}
      particlesElementId='particles-js'
      title={get(msg, 'title')}
    />
  );
};

export const Maintenance = memo(BareMaintenance);
